import type { Picture as PictureType } from '@/@types/common'
import { css } from '@emotion/react'
import { forwardRef, useRef, type HTMLAttributes, type MouseEvent } from 'react'
import { Gallery, Item as GalleryItem } from 'react-photoswipe-gallery'
import Picture, { PictureHandle, PictureProps } from './Picture'

export interface PictureGridProps<DataItem extends PictureType = PictureType>
	extends HTMLAttributes<HTMLDivElement> {
	data: DataItem[] | undefined
	mode?: 'complex' | 'simple'
	onItemClick?: (
		data: DataItem,
		index: number,
		e: MouseEvent<Element, globalThis.MouseEvent>
	) => void
}

function getItemStyle(mode: PictureGridProps['mode'], total: number, index: number) {
	if (mode !== 'complex') {
		return css`
			max-width: 140px;
			max-height: 140px;
		`
	}

	if (total === 1) {
		return css`
			max-height: 260px;
			max-width: 260px;
		`
	}

	const restAmount = total % 3
	if (restAmount === 0 || index < total - restAmount) {
		return css`
			height: 174px;
			aspect-ratio: 1 / 1;
			flex-basis: calc((100% - 12px) / 3);
		`
	}

	if (restAmount === 1) {
		return css`
			height: 174px;
			flex-basis: calc((100% - 6px) / 2);
		`
	}

	return css`
		height: 174px;
		flex-basis: calc((100% - ${(restAmount - 1) * 6}px) / ${restAmount});
	`
}

const PictureGridItem = forwardRef<
	HTMLDivElement,
	{
		picture: PictureType
		index: number
		mode: PictureGridProps['mode']
		total: number
	} & Pick<HTMLAttributes<Element>, 'onClick'>
>(function PictureGridItem({ picture, index, mode, total, onClick }, ref) {
	const height = mode === 'complex' ? (total === 1 ? 260 : 174) : undefined

	return (
		<div
			ref={ref}
			css={css`
				overflow: hidden;
				${getItemStyle(mode, total, index)};
			`}
		>
			<Picture
				isLivePhoto={!!picture.livePhoto}
				data={picture.livePhoto}
				isPreview
				src={picture.smallPicUrl}
				height={height}
				onClick={onClick}
				data-clickable-feedback={!!onClick}
				css={css`
					object-fit: cover;
					border-radius: 8px;
					border: 1px solid var(--tint-pic-border, rgb(0 0 0 / 5%));
					display: block;
					width: 100%;
					overflow: hidden;
				`}
			/>
		</div>
	)
})

function PictureSlideItem(props: PictureProps) {
	const ref = useRef<PictureHandle | null>(null)

	return (
		<Picture
			{...props}
			ref={ref}
			onCanPlay={() => {
				ref.current?.livePhoto?.player?.play()
			}}
		/>
	)
}

export default function PictureGrid<DataItem extends PictureType = PictureType>({
	data,
	mode = 'simple',
	onItemClick,
	...props
}: PictureGridProps<DataItem>) {
	if (!data || data.length === 0) {
		return null
	}

	return (
		<Gallery>
			<div
				css={css`
					display: flex;
					gap: 6px;
					flex-wrap: wrap;
				`}
				{...props}
			>
				{data.map((picture, index) => (
					<GalleryItem
						key={`${picture.key}_${index}_wrapper`}
						thumbnail={picture.thumbnailUrl}
						original={picture.picUrl}
						width={picture.width}
						height={picture.height}
						content={
							picture.livePhoto && (
								<PictureSlideItem
									isLivePhoto
									data={picture.livePhoto}
									src={picture.picUrl}
									width={picture.width}
									height={picture.height}
								/>
							)
						}
					>
						{({ ref, open }) => (
							<PictureGridItem
								ref={ref}
								key={`${picture.key}_${index}`}
								picture={picture}
								index={index}
								mode={mode}
								total={data.length}
								onClick={(e) => {
									e.stopPropagation()
									if (onItemClick) {
										onItemClick(picture, index, e)
									} else {
										open(e)
									}
								}}
							/>
						)}
					</GalleryItem>
				))}
			</div>
		</Gallery>
	)
}
